/**
 * SFTP connectors and SFTP pull retrieval (TO-393, Epic 25 F4).
 *
 * A connector is one customer SFTP endpoint of a tenant, backed by one AWS
 * Transfer Family SFTP connector and one Secrets Manager secret holding a
 * TODAY-generated private key. No shape here ever carries the private key, the
 * secret name or the AWS connector id.
 */
import type { LogicalFileType } from './file-exchange.interface.js'

/** Whether a connector's server host key is trusted (BR-2). Retrieval needs `TRUSTED`. */
export enum HostKeyStatus {
	UNCONFIRMED = 'UNCONFIRMED',
	TRUSTED = 'TRUSTED',
}

/** How the trusted host key became trusted (BR-3, BR-4). */
export enum HostKeyTrustSource {
	/** A Super-Admin confirmed the observed key (trust on first use). */
	TOFU_CONFIRMED = 'TOFU_CONFIRMED',
	/** A test observed a key whose fingerprint was pinned. */
	PINNED_MATCH = 'PINNED_MATCH',
}

/** The result of "Test connection" (BR-3). */
export enum ConnectorTestOutcome {
	CONNECTED = 'CONNECTED',
	FAILED = 'FAILED',
}

/** How one scheduled retrieval run of a logical file ended (BR-17). */
export enum RetrievalRunOutcome {
	/** The file was no longer due when the run started. */
	SKIPPED = 'SKIPPED',
	NO_FILE = 'NO_FILE',
	FILES_SELECTED = 'FILES_SELECTED',
	RETRIEVAL_FAILED = 'RETRIEVAL_FAILED',
}

/** Where a retrieval run is (design D5): listing requested, transfers being started, finished. */
export enum RetrievalRunPhase {
	LISTING = 'LISTING',
	TRANSFERRING = 'TRANSFERRING',
	DONE = 'DONE',
}

/** The state of one remote file version (BR-14). */
export enum RetrievalStateStatus {
	IN_PROGRESS = 'IN_PROGRESS',
	RETRIEVED = 'RETRIEVED',
	FAILED = 'FAILED',
}

/**
 * Why a retrieval run or one file's retrieval failed (spec Contract). Stored
 * as a run's `failureCode` and as an F2 execution's `failureCode`.
 */
export enum SftpRetrievalFailureCode {
	CONNECTOR_NOT_FOUND = 'CONNECTOR_NOT_FOUND',
	POST_RETRIEVAL_UNSUPPORTED = 'POST_RETRIEVAL_UNSUPPORTED',
	HOST_KEY_UNCONFIRMED = 'HOST_KEY_UNCONFIRMED',
	SFTP_CONNECTION_FAILED = 'SFTP_CONNECTION_FAILED',
	SFTP_AUTH_FAILED = 'SFTP_AUTH_FAILED',
	SFTP_HOST_KEY_MISMATCH = 'SFTP_HOST_KEY_MISMATCH',
	SFTP_REMOTE_PATH_NOT_FOUND = 'SFTP_REMOTE_PATH_NOT_FOUND',
	SFTP_PERMISSION_DENIED = 'SFTP_PERMISSION_DENIED',
	SFTP_LISTING_FAILED = 'SFTP_LISTING_FAILED',
	SFTP_LISTING_TRUNCATED = 'SFTP_LISTING_TRUNCATED',
	SFTP_REMOTE_FILE_NOT_FOUND = 'SFTP_REMOTE_FILE_NOT_FOUND',
	SFTP_TRANSFER_TIMEOUT = 'SFTP_TRANSFER_TIMEOUT',
	SFTP_TRANSFER_FAILED = 'SFTP_TRANSFER_FAILED',
	REMOTE_FILENAME_UNSUPPORTED = 'REMOTE_FILENAME_UNSUPPORTED',
}

/** A host key the connector trusts. */
export interface TrustedHostKeyDTO {
	algorithm: string
	/** `SHA256:` + 43 unpadded base64 characters. */
	fingerprint: string
	trustedAt: string
	trustedBy: string
	source: HostKeyTrustSource
}

/** The host key a server presented to the last test. */
export interface ObservedHostKeyDTO {
	algorithm: string
	fingerprint: string
	/** One OpenSSH line: `<algorithm> <base64>`. */
	publicKey: string
	observedAt: string
}

export interface ConnectorLastTestDTO {
	testedAt: string
	outcome: ConnectorTestOutcome
	failureCode: SftpRetrievalFailureCode | null
}

/** The connector's newest retrieval run that is not `SKIPPED` (BR-17). */
export interface ConnectorLastRetrievalDTO {
	at: string
	logicalFile: LogicalFileType
	outcome: RetrievalRunOutcome
	failureCode: SftpRetrievalFailureCode | null
}

/** One SFTP connector, as the Super-Admin routes return it. */
export interface SftpConnectorDTO {
	id: string
	name: string
	host: string
	port: number
	username: string
	/** The OpenSSH public key line the customer installs (`ssh-rsa <base64>`). */
	publicKey: string
	publicKeyFingerprint: string
	/** The static IPs the connector connects from — the customer allowlists them. */
	egressIpAddresses: string[]
	hostKeyStatus: HostKeyStatus
	trustedHostKey: TrustedHostKeyDTO | null
	expectedHostKeyFingerprints: string[]
	lastObservedHostKey: ObservedHostKeyDTO | null
	lastTest: ConnectorLastTestDTO | null
	lastRetrieval: ConnectorLastRetrievalDTO | null
	/** Logical files whose current configuration names the connector, in F1 order. */
	usedBy: LogicalFileType[]
	createdAt: string
	createdBy: string
	updatedAt: string
	updatedBy: string
}

export interface SftpConnectorListDTO {
	items: SftpConnectorDTO[]
}

/** `POST …/connectors/{connectorId}/test` response. A failed connection is still a 200. */
export interface ConnectorTestResultDTO {
	outcome: ConnectorTestOutcome
	failureCode: SftpRetrievalFailureCode | null
	observedHostKey: Omit<ObservedHostKeyDTO, 'observedAt'> | null
	connector: SftpConnectorDTO
}

/** `POST …/connectors` body. No password or private key is accepted. */
export interface SftpConnectorCreateRequest {
	name: string
	host: string
	/** Default 22. */
	port?: number
	username: string
	/** Default `[]`. */
	expectedHostKeyFingerprints?: string[]
}

/** `PATCH …/connectors/{connectorId}` body: any non-empty subset of the create fields. */
export type SftpConnectorUpdateRequest = Partial<SftpConnectorCreateRequest>

/** `POST …/connectors/{connectorId}/host-key/confirm` body. */
export interface HostKeyConfirmRequest {
	fingerprint: string
}
